import { Injectable } from '@angular/core';
import { chunk } from 'lodash';
import { AppStorage } from 'src/app/shared/app-storage';
import { EnvironmentService } from 'src/environments/environment.service';
import { AbstractAccountKeyring } from '../crypto/keyring/account_base';
import { PeerAccountKeyring } from '../crypto/keyring/account_peer';
import { CacheService } from '../services/cache/cache.service';
import { ServerRestApiService } from '../services/server-rest-api.service';
import { AuthService } from './auth.service';
import {
  AccountAvatarRecord,
  AccountKeysRecord,
  AccountSharedSecretsRecord,
  AccountVariantRecord,
  DialoguePolicy,
  MeRecord,
  UserRole,
} from './query-records/account-records';
import { PrivyRoomRecord } from './query-records/room-records';
import {
  accountAvatarsQuery,
  accountKeyringVariantQuery,
  createSSQuery,
  editDialogueHidden,
  editDialoguePolicy,
  editEmailQuery,
  editTwoFactorModeQuery,
  getAccountKeysQuery,
  getAccountQuery,
  getAllAccountIdQuery,
  getMeQuery,
  getTwoFactorModeQuery,
  modifyAvatarNameQuery,
  modifyAvatarQuery,
  prepareEditEmailQuery,
  resaveSSQuery,
  sharedSecretsQuery,
} from './querys';
import { AbstractPreparePeerKeyringMlKem } from '../crypto/common/kem';
import { SelfAccountKeyring } from '../crypto/keyring/account_self';
export const enum UserVerificationState {
  VERIFIED,
  NOT_VERIFIED,
  DANGEROUS,
}

export type CachedPeerKeyring = {
  peerKr: PeerAccountKeyring,
  lastKemCheck?: Date
}

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private batchAccountId: string[] = [];
  private isGetAccountQueryStarted: boolean = false;
  /**
   * [resolve, reject] callbacks pair
   */
  private getAccountCallbacks: [Function, Function][] = [];
  /**
   * waiting and collecting time before starting the request in millisec
   */
  public static BATCH_TIME_FOR_ACCOUNTS_QUERY: number = 50;

  public gatewayPreparePeerKeyringMlKem: GatewayPreparePeerKeyringMlKem;

  constructor(
    private serverRestApiService: ServerRestApiService,
    private authService: AuthService,
    private cacheService: CacheService,
    private environmentService: EnvironmentService
  ) {
    this.gatewayPreparePeerKeyringMlKem = new GatewayPreparePeerKeyringMlKem(
      serverRestApiService,
      this
    );
  }

  private cachedPeerKeyring: { [key: string]: CachedPeerKeyring } = {}; // accondId => peerKeyring
  private READY_KEM_CHECK_TIME = 300; // seconds

  public getMe(): Promise<MeRecord> {
    return this.serverRestApiService.query({ query: getMeQuery });
  }

  // stored avatar images as blob
  private storedAvatarImages: { [key: string]: Blob } = {};

  // promise queue for fetching avatar images
  private fetchAvatarImages: { [key: string]: [Function, Function][] } = {};

  public getAvatarImage(avatar: AccountAvatarRecord, forceFetch: boolean = false): Promise<Blob> {
    // reset cache
    if (forceFetch) {
      delete this.storedAvatarImages[avatar.id];
    }

    // use cached blob if can
    let blob = this.storedAvatarImages[avatar.id];
    if (blob) {
      return Promise.resolve(blob);
    }

    return new Promise((resolve, reject) => {
      if (!this.fetchAvatarImages[avatar.id]) {
        // there is no queue for query, so make one
        this.fetchAvatarImages[avatar.id] = [[resolve, reject]];

        // start fetching the image
        let src =
          this.environmentService.url_api + '/r/avatar/' + avatar.id + '/' + avatar.avatarImageKey;
        fetch(src)
          .then((res) => {
            return res.blob().then((blob) => {
              this.storedAvatarImages[avatar.id] = blob;
              return blob;
            });
          })
          .catch((err) => {
            console.warn('can not load avatar', err, avatar.id);
            let blob = new Blob([]);
            this.storedAvatarImages[avatar.id] = blob;
            return blob;
          })
          .then((blob) => {
            // call all promise in the queue
            this.fetchAvatarImages[avatar.id].forEach(([resolve, reject]) => {
              resolve(blob);
            });
            delete this.fetchAvatarImages[avatar.id];
            return blob;
          });
      } else {
        // just put in the queue
        this.fetchAvatarImages[avatar.id].push([resolve, reject]);
      }
    });
  }

  /**
   *
   * @param id
   * @param forceFetch There is no avatar change event currently, so we have to force to refetch the user
   * @returns
   */
  public getAccount(id: string, forceFetch = false): Promise<AccountAvatarRecord> {
    // return immediately when possible
    if (forceFetch) {
      return this.serverRestApiService.query({
        query: getAccountQuery,
        variables: { id },
      });
    } else {
      let acc = this.cacheService.getCacheDataByRequest({
        query: getAccountQuery,
        variables: { id },
      });
      if (acc) {
        return Promise.resolve(acc);
      } else {
        return this.getAccounts([id]).then((accs) => {
          return accs[id];
        });
      }
    }
  }

  /**
   *
   * @param {string[]} accountIds one or more ids
   * @return {Promise<{[key:string]: AccountAvatarRecord}>} result
   */
  public getAccounts(accountIds: string[]): Promise<{ [key: string]: AccountAvatarRecord }> {
    let result: { [key: string]: AccountAvatarRecord } = {};
    let notFoundList: string[] = [];
    accountIds.forEach((id) => {
      let acc = <AccountAvatarRecord>(
        this.cacheService.getCacheDataByRequest({ query: getAccountQuery, variables: { id } })
      );
      if (acc) {
        result[id] = acc;
      } else {
        notFoundList.push(id);
      }
    });

    if (notFoundList.length == 0) {
      // return immediately when possible
      return Promise.resolve(result);
    } else {
      // start an async batch query
      return new Promise((resolve, reject) => {
        // merge ids
        notFoundList.forEach((id) => {
          if (!this.batchAccountId.includes(id)) {
            this.batchAccountId.push(id);
          }
        });

        // save callbacks after query response
        this.getAccountCallbacks.push([resolve, reject]);

        if (!this.isGetAccountQueryStarted) {
          this.isGetAccountQueryStarted = true;

          // wait for a little bit and call the query
          window.setTimeout(() => {
            let list = this.batchAccountId;
            let callbacks = this.getAccountCallbacks;
            this.batchAccountId = [];
            this.getAccountCallbacks = [];
            this.isGetAccountQueryStarted = false;
            this.queryAccounts(list, callbacks);
          }, AccountService.BATCH_TIME_FOR_ACCOUNTS_QUERY);
        }
      }).then((accountsFromQuery) => {
        // we can get more accounts, because it is all of the batched request's response
        notFoundList.forEach((id) => {
          result[id] = accountsFromQuery[id];
        });
        return result;
      });
    }
  }

  /**
   *
   * @param idList id list
   * @param callbacks (resolve, reject) promise callbacks
   * @returns all the batched id
   */
  public queryAccounts(idList: string[], callbacks: [Function, Function][]) {
    let batchSize: number = 500;
    let idBatches: Array<Array<string>> = chunk(idList, batchSize);
    let batchPromises: Array<Promise<AccountAvatarRecord[]>> = [];

    idBatches.forEach((idBatch, i) => {
      let delayMs = i * 200;
      let delayedQueryPromise = new Promise((resolve) => setTimeout(resolve, delayMs)).then(() => {
        return this.serverRestApiService.query({
          query: accountAvatarsQuery,
          variables: { ids: idBatch },
        });
      });

      batchPromises.push(delayedQueryPromise);
    });

    Promise.all(batchPromises)
      .then((accountBatches) => {
        let allAccountAvatars: { [key: string]: AccountAvatarRecord } = {};

        accountBatches.forEach((accountsQueryResponse) => {
          accountsQueryResponse.forEach((account) => {
            allAccountAvatars[account.id] = account;
          });
        });

        callbacks.forEach(([resolve, reject]) => {
          resolve(allAccountAvatars);
        });
      })
      .catch((e) => {
        callbacks.forEach(([resolve, reject]) => {
          reject(e);
        });
      });
  }

  /**
   * Get the all direct contact from your workspace.
   */
  public getAllAccount(): Promise<AccountAvatarRecord[]> {
    return this.serverRestApiService
      .query({
        query: getAllAccountIdQuery,
      })
      .then((ids: string[]) => {
        return this.getAccounts(ids).then((accs) => {
          return Object.values(accs);
        });
      });
  }

  public getAccountKeys(id): Promise<AccountKeysRecord> {
    return this.serverRestApiService.query({
      query: getAccountKeysQuery,
      variables: {
        id,
      },
    });
  }

  public getAccountVariant(id): Promise<AccountVariantRecord> {
    return this.serverRestApiService.query({
      query: accountKeyringVariantQuery,
      variables: { id },
    });
  }

  /**
   * check if peer_kr was checked in the last 3-5 minutes
   * @returns
   */
  private shouldReadyKem(accountId) {
    let validUntil = new Date();
    validUntil.setSeconds(validUntil.getSeconds() - this.READY_KEM_CHECK_TIME);
    return !this.cachedPeerKeyring[accountId]?.lastKemCheck || this.cachedPeerKeyring[accountId].lastKemCheck < validUntil;
  }

  /**
   *
   * @param self_kr
   * @param peer_kr
   * @param require_self_ss
   * @param limit_proactive_polling call readyPeerKrKem with limit_proactive_polling=true from proactive (dump) contexts
   * @returns
   */
  public async readyPeerKrKem(
    self_kr: SelfAccountKeyring,
    peer_kr: PeerAccountKeyring,
    require_self_ss: boolean = false,
    limit_proactive_polling: boolean = false
  ): Promise<PeerAccountKeyring> {
    const accountId = new TextDecoder().decode(peer_kr.id);

    // if (limit_proactive_polling) return if peer_kr was checked in the last 3-5 minutes
    if (limit_proactive_polling && !this.shouldReadyKem(accountId)){
      return peer_kr;
    }

    const new_kr = await this.gatewayPreparePeerKeyringMlKem.ready_peer_kr_kem(
      self_kr,
      peer_kr,
      require_self_ss
    );
    if (peer_kr != new_kr) {
      //const accountId = new TextDecoder().decode(new_kr.id);
      this.updatePeerKeyring(accountId, new_kr, true);
    }
    else{
      this.updatePeerKeyringLastKemCheck(accountId);
    }
    return new_kr;
  }

  public getAccountSharedSecrets(id): Promise<AccountSharedSecretsRecord> {
    return this.serverRestApiService.query({
      query: sharedSecretsQuery,
      variables: { id },
    });
  }

  public async resavePeerKeyringKemImpl(
    accountId: string,
    peer_kr: PeerAccountKeyring,
    resp?: AccountSharedSecretsRecord
  ) {
    if (resp && resp.ssPeerMlSignature) {
      const enc_p_ss = await peer_kr.resave_kem(
        this.authService.getSelfAccountKeyring(),
        PeerAccountKeyring.MLKEM1024
      );
      await this.serverRestApiService.mutate({
        query: resaveSSQuery,
        variables: { peerId: accountId, ssPeerMl: enc_p_ss },
      });
    }
  }

  public getPeerKeyring(accountId, force = false): Promise<PeerAccountKeyring> {
    if (!force && accountId in this.cachedPeerKeyring) {
      return Promise.resolve(this.cachedPeerKeyring[accountId].peerKr);
    } else {
      return this.getAccountKeys(accountId).then((keys) => {
        return PeerAccountKeyring.load(
          this.authService.getSelfAccountKeyring(),
          accountId,
          keys.encryptingPublicEc,
          keys.encryptingPublicEcSignature,
          keys.signingPublicEc,
          keys.trust,
          null,
          keys.encryptingPublicMl,
          keys.encryptingPublicMlSignature,
          keys.signingPublicMl,
          keys.signingEcPublicMlPublicSignature,
          keys.ssSelfMl,
          keys.ssPeerMl,
          keys.ssPeerMlSignature
        ).then((peer_kr) => {
          this.cachedPeerKeyring[accountId] = { peerKr: peer_kr };
          return peer_kr;
        });
      });
    }
  }

  public updatePeerKeyring(accountId: string, peerKr: PeerAccountKeyring, updateLastKemCheck = false): void {
    this.cachedPeerKeyring[accountId] = { peerKr };
    if (updateLastKemCheck){
      this.cachedPeerKeyring[accountId].lastKemCheck = new Date();
    }
  }

  private updatePeerKeyringLastKemCheck(accountId){
    this.cachedPeerKeyring[accountId].lastKemCheck = new Date();
  }

  public getPrivyPeerKeyring(privy: PrivyRoomRecord) {
    if (privy.roomId in this.cachedPeerKeyring) {
      return Promise.resolve(this.cachedPeerKeyring[privy.roomId].peerKr);
    }
    return PeerAccountKeyring.load(
      this.authService.getSelfAccountKeyring(),
      privy.roomId,
      privy.privyEncryptingPublicEc,
      privy.privyEncryptingPublicEcSignature,
      privy.privySigningPublicEc,
      null,
      null,
      privy.privyEncryptingPublicMl,
      privy.privyEncryptingPublicMlSignature,
      privy.privySigningPublicMl,
      privy.privySigningEcPublicMlPublicSignature
    ).then((peer_kr) => {
      this.cachedPeerKeyring[privy.roomId] = { peerKr: peer_kr };
      return peer_kr;
    });
  }

  public setAvatar(avatarImage): Promise<any> {
    return this.serverRestApiService.mutate({
      query: modifyAvatarQuery,
      variables: {
        image: avatarImage,
      },
    });
  }

  public changeAvatarName(avatarName: string): Promise<boolean> {
    return this.serverRestApiService.mutate({
      query: modifyAvatarNameQuery,
      variables: {
        name: avatarName,
      },
    });
  }

  public get2FA(): Promise<any> {
    return this.serverRestApiService.query({
      query: getTwoFactorModeQuery,
    });
  }

  public set2FA(enabled: boolean): Promise<boolean> {
    var mode = enabled ? 10 : 0;
    let params = {
      mode,
    };
    if (AppStorage.getItem('lang')) {
      params['locale'] = AppStorage.getItem('lang');
    }

    return this.serverRestApiService.mutate({
      query: editTwoFactorModeQuery,
      variables: params,
    });
  }

  public prepareEditEmail(newEmail: string): Promise<boolean> {
    let params = {
      email: newEmail,
    };

    if (AppStorage.getItem('lang')) {
      params['locale'] = AppStorage.getItem('lang');
    }

    return this.serverRestApiService.mutate({
      query: prepareEditEmailQuery,
      variables: params,
    });
  }

  public editEmail(token: string): Promise<boolean> {
    let params = {
      token,
    };

    if (AppStorage.getItem('lang')) {
      params['locale'] = AppStorage.getItem('lang');
    }
    return this.serverRestApiService.mutate({
      query: editEmailQuery,
      variables: params,
    });
  }

  public isCurrentUserInRole(role: UserRole, me: MeRecord): boolean {
    return me.scopes.some((scope) => scope == role);
  }

  public startDeleteProcess(avatarId: string): Promise<any> {
    return this.serverRestApiService.makeAjaxPost('debug/account/delete', { id: avatarId });
  }

  public changeDialoguePolicy(policy: DialoguePolicy): Promise<any> {
    return this.serverRestApiService.mutate({
      query: editDialoguePolicy,
      variables: { policy: policy },
    });
  }

  public blockUser(userId: string): Promise<any> {
    return this.serverRestApiService.mutate({
      query: editDialogueHidden,
      variables: { peerId: userId, hidden: true },
    });
  }

  public unblockUser(userId: string): Promise<any> {
    return this.serverRestApiService.mutate({
      query: editDialogueHidden,
      variables: { peerId: userId, hidden: false },
    });
  }

  public getUserVerificationState(userId: string): Promise<UserVerificationState> {
    return this.getPeerKeyring(userId).then((peer_kr) => {
      if (peer_kr.trusted == AbstractAccountKeyring.TRUSTED_FULLY)
        return UserVerificationState.VERIFIED;
      if (peer_kr.trusted == AbstractAccountKeyring.TRUSTED_NOT)
        return UserVerificationState.DANGEROUS;
      return UserVerificationState.NOT_VERIFIED;
    });
  }
}

export class GatewayPreparePeerKeyringMlKem extends AbstractPreparePeerKeyringMlKem {
  constructor(
    private restApiService: ServerRestApiService,
    private accountService: AccountService
  ) {
    super();
  }

  /**
   * Check if the keyring can be upgraded and if the server has a new variant
   * of the peer keyring.
   */
  public async _check_peer_kr_variant(
    self_kr: SelfAccountKeyring,
    peer_kr: PeerAccountKeyring
  ): Promise<PeerAccountKeyring> {
    const accountId = new TextDecoder().decode(peer_kr.id);

    try {
      const res = await this.accountService.getAccountVariant(accountId);

      if (res?.variant === PeerAccountKeyring.VARIANT_ML) {
        return await this.accountService.getPeerKeyring(accountId, true);
      }

      return peer_kr;
    } catch (error) {
      console.error('Check peer kr variant error', error);
      throw error;
    }
  }

  /**
   * Check if there are not loaded shared secrets for the peer keyring from the server.
   * This could be optional if the client keeps the peer keyring objects in sync with the event stream.
   */
  public async _check_peer_kr_ss(
    self_kr: SelfAccountKeyring,
    peer_kr: PeerAccountKeyring
  ): Promise<PeerAccountKeyring> {
    const accountId = new TextDecoder().decode(peer_kr.id);

    console.log('check peer kr ss for user', accountId);

    try {
      const res = await this.accountService.getAccountSharedSecrets(accountId);

      console.log('shared sercets res', res);

      const updated = await peer_kr.update_shared_secrets(
        self_kr,
        res.ssSelfMl,
        res.ssPeerMl,
        res.ssPeerMlSignature
      );

      console.log('updated ss', updated);

      if (updated) {
        await this.accountService.resavePeerKeyringKemImpl(accountId, peer_kr, res);
      }

      return peer_kr;
    } catch (error) {
      console.error('Check peer kr ss error', error);
      throw error;
    }
  }

  /**
   * Send save request to server and wait for acknowledgemenet.
   */
  public async _save_kem(
    self_kr: SelfAccountKeyring,
    peer_kr: PeerAccountKeyring,
    enc_ss: Uint8Array,
    kem: Uint8Array,
    kem_sig: Uint8Array
  ): Promise<void> {
    const accountId = new TextDecoder().decode(peer_kr.id);
    try {
      const res = await this.restApiService.mutate({
        query: createSSQuery,
        variables: {
          peerId: accountId,
          ssSelfMl: enc_ss,
          ssPeerMl: kem,
          ssPeerMlSignature: kem_sig,
        },
      });
    } catch (error) {
      console.error('save kem error', error);
      throw error;
    }
  }
  /**
   * Client application can listen to an event stream to update the self.peer_kr and return it.
   * Specifically the relevant SS_SELF needs to be waited.
   * It can also poll and return if the required or any ss is received.
   * This shall have a time limit to not block forever by accident, but no other failure branches need to be handled.
   */
  public async _wait_get_peer_kr(
    self_kr: SelfAccountKeyring,
    peer_kr: PeerAccountKeyring
  ): Promise<PeerAccountKeyring> {
    await new Promise((resolve) => setTimeout(resolve, 100));

    const accountId = new TextDecoder().decode(peer_kr.id);

    try {
      for (let index = 0; index < 20; index++) {
        const res = await this.accountService.getAccountSharedSecrets(accountId);

        if (res.ssSelfMl) {
          const updated = await peer_kr.update_shared_secrets(
            self_kr,
            res.ssSelfMl,
            res.ssPeerMl,
            res.ssPeerMlSignature
          );
          if (updated) {
            await this.accountService.resavePeerKeyringKemImpl(accountId, peer_kr, res);
          }
          return peer_kr;
        }

        await new Promise((resolve) => setTimeout(resolve, 150));
      }
      throw new Error('Updated keyring was not found');
    } catch (error) {
      console.error('wait get peer kr error', error);
      throw error;
    }
  }
}
